export const validateExpiry = (value) => {
  if (!value || value.length !== 5) {
    return 'Expiry date is required';
  }
  const [monthStr, yearStr] = value.split('/');
  const month = parseInt(monthStr, 10);
  const year = 2000 + parseInt(yearStr, 10);
  if (isNaN(month) || isNaN(year)) {
    return 'Invalid expiry date';
  }
  if (month < 1 || month > 12) {
    return 'Month must be between 01 and 12';
  }
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return 'Expiry date must be in the future';
  }

  const maxYear = currentYear + 25;
  if (year > maxYear) {
    return `Expiry year must not exceed ${maxYear}`;
  }

  return true;
};

/**
 * Detects card network from leading digits.
 * Returns type, valid lengths, space-gap positions, and CVC length.
 */
export const detectCardType = (number) => {
  const d = (number || '').replace(/\D/g, '');
  if (/^3[47]/.test(d)) return { type: 'amex', lengths: [15], gaps: [4, 10], cvcLength: 4 };
  if (/^3(?:0[0-5]|[68])/.test(d)) return { type: 'diners', lengths: [14, 16], gaps: [4, 10], cvcLength: 3 };
  if (/^6(?:011|5)/.test(d)) return { type: 'discover', lengths: [16, 19], gaps: [4, 8, 12], cvcLength: 3 };
  if (/^(?:5[1-5]|2[2-7])/.test(d)) return { type: 'mastercard', lengths: [16], gaps: [4, 8, 12], cvcLength: 3 };
  if (/^35(?:2[89]|[3-8])/.test(d)) return { type: 'jcb', lengths: [16, 17, 18, 19], gaps: [4, 8, 12], cvcLength: 3 };
  if (/^62/.test(d)) return { type: 'unionpay', lengths: [16, 17, 18, 19], gaps: [4, 8, 12], cvcLength: 3 };
  if (/^4/.test(d)) return { type: 'visa', lengths: [13, 16, 19], gaps: [4, 8, 12], cvcLength: 3 };
  return { type: 'unknown', lengths: [13, 14, 15, 16, 17, 18, 19], gaps: [4, 8, 12], cvcLength: 3 };
};

/**
 * Formats a card number string with spaces at the appropriate gap positions
 * based on the detected card type (e.g. 4-6-5 for Amex, 4-4-4-4 for Visa).
 */
export const formatCardNumber = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 19);
  const { gaps } = detectCardType(digits);
  let formatted = '';
  for (let i = 0; i < digits.length; i++) {
    if (gaps.includes(i)) formatted += ' ';
    formatted += digits[i];
  }
  return formatted;
};

/**
 * Validates a credit card number:
 * 1. Strips non-digit characters
 * 2. Checks length is valid for the detected card type
 * 3. Runs the Luhn (mod-10) checksum
 */
export const validateCardNumber = (number) => {
  if (!number) return false;
  const sanitized = number.replace(/\D/g, '');

  if (sanitized.length < 13 || sanitized.length > 19) return false;

  const { lengths } = detectCardType(sanitized);
  if (!lengths.includes(sanitized.length)) return false;

  let sum = 0;
  let shouldDouble = false;
  for (let i = sanitized.length - 1; i >= 0; i--) {
    let digit = parseInt(sanitized[i], 10);
    if (isNaN(digit)) return false;
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

export const CARD_ICONS = {
  visa: 'fab fa-cc-visa',
  mastercard: 'fab fa-cc-mastercard',
  amex: 'fab fa-cc-amex',
  discover: 'fab fa-cc-discover',
  diners: 'fab fa-cc-diners-club',
  jcb: 'fab fa-cc-jcb',
  unionpay: 'fas fa-credit-card',
  unknown: 'fas fa-credit-card',
};

export const buildPayload = (paymentDetails, formData, token, userDetails, case_id) => {
  const case_name = paymentDetails?.case_name || paymentDetails?.invoice?.case_name;
  const payment_ref_number = paymentDetails?.invoice_number || paymentDetails?.payment_request_number;
  const billing_contact_id = paymentDetails?.billing_contact_id || paymentDetails?.invoice?.contact_id;
  const billing_contact_name = paymentDetails?.billing_contact_name || paymentDetails?.invoice?.contact_name;
  const isInvoice = paymentDetails?.res_type === 'invoice';

  return {
    token,
    save_card: token && !!formData?.save_card,
    card_on_file: formData?.selectedCard?.id,
    amount: formData?.amount,
    billing_contact_id,
    billing_contact_name,
    case_id,
    case_name,
    deposit_account_id: paymentDetails?.account_id || paymentDetails?.deposit_account_id,
    deposit_account_name: paymentDetails?.deposit_account_name,
    deposit_into: paymentDetails?.account_type || paymentDetails?.deposit_account_type || paymentDetails?.deposit_into,
    description: 'Payment made by client via intuit',
    due_amount: paymentDetails?.due_amount,
    paid_amount: String(paymentDetails?.paid_amount || 0),
    payment_for: isInvoice ? 'invoice' : 'payment_request',
    payment_method: 'Credit Card',
    payment_record_amount: Number(formData?.amount),
    payment_record_date: new Date(),
    payment_record_status: Number(formData?.amount) === Number(paymentDetails?.due_amount) ? 'FULL' : 'PARTIAL',
    payment_record_type: 'ONLINE',
    payment_ref_id: paymentDetails?.pk?.split('#')?.[1],
    payment_ref_number,
    payment_ref_qb_id: userDetails?.qb_id,
  };
};
